import { normalizeStorefrontPath } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useStorefrontHeadStore } from '../stores/storefrontHeadStore';

/**
 * Generic 404 page. Intentionally minimal — no details about why the page
 * wasn't found, just a clean dead-end so the user knows to check their URL.
 *
 *   - `fullPage` (default): the storefront itself could not be found, so
 *     there is no layout around it and no home page to link to.
 *   - `inline` (PD-17174): inside the storefront layout, for a URL the
 *     storefront does not serve or a product that does not exist, with a way
 *     back to the home page. It replaced the old catch-all redirect to `/`,
 *     which answered every mistyped or removed URL with the home page (a
 *     "soft 404" search engines penalize).
 *
 * Either way it reports the page as not found, so StorefrontHead marks it
 * `noindex` (the edge function has already answered a crawler with HTTP 404).
 * `reportNotFound` false is for a page shown after a failed request, which is
 * not evidence the page is gone.
 */
export function NotFoundPage({
	variant = 'fullPage',
	reportNotFound = true,
}: {
	variant?: 'fullPage' | 'inline';
	reportNotFound?: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const setNotFound = useStorefrontHeadStore((s) => s.setNotFound);
	const clearHead = useStorefrontHeadStore((s) => s.clear);

	useEffect(() => {
		if (!reportNotFound) {
			return;
		}
		const path = normalizeStorefrontPath(pathname);
		setNotFound(path);
		return (): void => {
			clearHead(path);
		};
	}, [pathname, reportNotFound, setNotFound, clearHead]);

	if (variant === 'inline') {
		return (
			<div className="max-w-4xl mx-auto px-4 py-12 text-center">
				<h1 className="text-5xl font-bold text-disabled mb-4">{t('404')}</h1>
				<p className="text-lg text-default mb-2">{t('Page not found')}</p>
				<p className="text-sm text-brand-muted mb-6">
					{t(
						'The page you are looking for does not exist or is no longer available.',
					)}
				</p>
				<Link
					to="/"
					className="text-sm font-medium text-portal-accent hover:underline"
				>
					{t('Go to the home page')}
				</Link>
			</div>
		);
	}

	return (
		<div className="min-h-dvh flex items-center justify-center bg-brand-page-bg px-4">
			<div className="text-center">
				<h1 className="text-6xl font-bold text-disabled mb-4">{t('404')}</h1>
				<p className="text-lg text-brand-muted mb-2">{t('Page not found')}</p>
				<p className="text-sm text-brand-muted">
					{t(
						'The page you are looking for does not exist or is no longer available.',
					)}
				</p>
			</div>
		</div>
	);
}
